// ---------------------------------------------------------------------------
// Sync status: a tiny external store (same pattern as db/events.ts) so the top
// bar chip and Settings screen can react to sync state without a full store.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from 'react';

export type SyncState = 'disabled' | 'offline' | 'syncing' | 'synced' | 'error' | 'conflicted';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  conflicts: number;
  lastSyncedAt: string | null;
  error: string | null;
}

let status: SyncStatus = {
  state: 'disabled',
  pending: 0,
  conflicts: 0,
  lastSyncedAt: null,
  error: null,
};

const listeners = new Set<() => void>();

export function getSyncStatus(): SyncStatus {
  return status;
}

export function setSyncStatus(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch };
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Non-React subscription that receives the status value (e.g. telemetry). */
export function subscribeSyncStatus(listener: (status: SyncStatus) => void): () => void {
  return subscribe(() => listener(status));
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(subscribe, getSyncStatus, getSyncStatus);
}
